import {msalInstance, msalLoginRequest} from '@config/azure.config';
import env from '@config/env.config';
import dayjs from '@utils/dayjs.utils';
import axios, {AxiosRequestConfig} from 'axios';
import jwtDecode from 'jwt-decode';

const APIAxios = axios.create({
  baseURL: env.API_URL || '',
});

APIAxios.interceptors.request.use(async (config) => {
  const request = {...msalLoginRequest, account: msalInstance.getAllAccounts()[0]};

  const authorization = (APIAxios.defaults.headers.common['Authorization'] as string) || '';

  if (authorization) {
    const token = authorization.replace('Bearer ', '') || '';
    const decodedToken = jwtDecode(token);

    const expirationDate = new Date((decodedToken as any).exp * 1000);

    if (dayjs(expirationDate).diff(undefined, 'm', true) > 5) {
      return config;
    }
  }

  try {
    const resp = await msalInstance.acquireTokenSilent(request);
    APIAxios.defaults.headers.common['Authorization'] = `Bearer ${resp.idToken}`;
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${resp.idToken}`,
    };
  } catch (err) {
    console.log('MSAL Error', err);
    await msalInstance.logoutRedirect();

    return Promise.reject(err);
  }
  return config;
});

export const APIRoutes = {
  /* AUTH */
  GETMe: (): AxiosRequestConfig => ({method: 'GET', url: '/user/about/me'}),

  /* STATISTICS */
  GETEvolutionOfOperations: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/stats/evolution'}),

  GETTotalTurnoverNew: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/stats/total-turnover-new'}),
  GETTurnoverAndPurchase: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/stats/turnover-and-purchase'}),
  GETMostProfitableClients: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/stats/most-profitable-clients'}),
  GETBarChart: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/stats/bar-chart'}),
  getInvoicingProgression: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/stats/invoicing-progression'}),
  GETMarginByLine: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/stats/margin-by-line'}),
  GETTurnoverChargesByServiceKind: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/stats/turnover-and-charges-by-service-kind'
  }),
  GETValoWeightByWasteFamily: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/stats/valo-weight-by-waste-family'
  }),
  GETExecutionCountByServiceKind: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/stats/exec-count-service-kind-distribution'
  }),

  /* CLIENTS */
  GETClients: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/client'}),
  GETClientsCSV: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/client/csv',
    responseType: 'blob',
    timeout: 0,
  }),

  POSTClient: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/client'}),
  GETClientById: (clientId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/client/${clientId}/details`,
  }),
  PUTClientById: (clientId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/client/${clientId}`,
  }),
  PATCHClientStatus: (clientId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/client/${clientId}/archive`,
  }),
  DELETEClient: (clientId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/client/${clientId}`,
  }),

  POSTClientContact: (clientId: number | string): AxiosRequestConfig => ({
    method: 'POST',
    url: `/admin/client/${clientId}/contact`,
  }),
  PUTClientContactById: (clientId: number | string, contactId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/client/${clientId}/contact/${contactId}`,
  }),
  DELETEClientContact: (clientId: number | string, contactId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/client/${clientId}/contact/${contactId}`,
  }),

  GETClientContact: (clientId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/client/${clientId}/contacts`,
  }),

  PUTClientFacturationModeById: (
    clientId: number | string,
    facturationModeId: number | string
  ): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/client/${clientId}/facturation-mode/${facturationModeId}`,
  }),

  /* CLIENTS UNITS */
  GETClientsUnits: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/client/units'}),
  GETClientsUnitsCSV: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/client/units/csv',
    responseType: 'blob',
    timeout: 0,
  }),

  POSTClientsUnit: (clientId: number | string): AxiosRequestConfig => ({
    method: 'POST',
    url: `/admin/client-unit/${clientId}/unit`,
  }),
  GETClientUnitById: (clientUnitId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/client-unit/${clientUnitId}/details`,
  }),
  PUTClientUnitById: (clientUnitId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/client-unit/${clientUnitId}`,
  }),
  PATCHClientsUnitStatus: (clientsUnitId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/client-unit/${clientsUnitId}/archive`,
  }),
  DELETEClientsUnit: (clientsUnitId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/client-unit/${clientsUnitId}`,
  }),

  POSTClientsUnitContact: (clientsUnitId: number | string): AxiosRequestConfig => ({
    method: 'POST',
    url: `/admin/client-unit/${clientsUnitId}/contact`,
  }),
  PUTClientsUnitContactById: (clientsUnitId: number | string, contactId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/client-unit/${clientsUnitId}/contact/${contactId}`,
  }),
  DELETEClientsUnitContact: (clientsUnitId: number | string, contactId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/client-unit/${clientsUnitId}/contact/${contactId}`,
  }),

  PUTClientUnitFacturationModeById: (
    clientUnitId: number | string,
    facturationModeId: number | string
  ): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/client-unit/${clientUnitId}/facturation-mode/${facturationModeId}`,
  }),

  /* CLIENTS SERVICES */
  GETClientsServices: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/client-unit-service'}),

  POSTClientsService: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/client-unit-service'}),
  POSTClientServiceWithSupplier: (): AxiosRequestConfig => ({
    method: 'POST',
    url: '/admin/client-unit-service/supplier'
  }),
  GETClientServiceById: (serviceId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/client-unit-service/${serviceId}`,
  }),
  POSTClientServicePrices: (): AxiosRequestConfig => ({
    method: 'POST',
    url: '/admin/client-unit-service/create-new-prices',
    timeout: 0
  }),
  PATCHClientServicesSettings: (): AxiosRequestConfig => ({
    method: 'PATCH',
    url: '/admin/client-unit-service/update-services',
  }),
  PATCHClientServiceById: (serviceId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/client-unit-service/${serviceId}`,
  }),
  PATCHClientServiceNewPrice: (serviceId: number | string): AxiosRequestConfig => ({
    method: 'POST',
    url: `/admin/client-unit-service/${serviceId}/add-price`,
  }),
  GETClientsServicesCSV: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/client-unit-service/csv',
    responseType: 'blob',
    timeout: 0,
  }),
  PATCHClientServiceKind: (serviceId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/client-unit-service/${serviceId}/update-service-kind`,
  }),
  DELETEClientServiceById: (serviceId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/client-unit-service/${serviceId}`,
  }),
  GETClientInvoiceAddresses: (clientId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/client/${clientId}/invoice-addresses`,
  }),
  PATCHInvoicingSource: (clientId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/client/${clientId}/invoicing-source`,
  }),
  PUTInvoiceAddress: (): AxiosRequestConfig => ({
    method: 'PUT',
    url: '/admin/client/invoice-address',
  }),
  PATCHWasteContainer: (contractId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/client-unit-service/${contractId}/update-waste-container`,
  }),


  /* CLIENTS CONTRACTS */
  GETClientsContracts: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/client-contracts'}),
  GETClientsContractsCSV: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/client-contracts/csv',
    responseType: 'blob',
    timeout: 0,
  }),
  GETClientContract: (contractId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/client-contracts/${contractId}`
  }),
  POSTClientsContract: (clientUnitId: number | string): AxiosRequestConfig => ({
    method: 'POST',
    url: `/admin/client-contracts/${clientUnitId}`,
  }),
  PATCHClientsContract: (contractId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/client-contracts/${contractId}`,
  }),

  /* SUPPLIERS */
  GETSuppliers: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/supplier'}),
  GETSuppliersCSV: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/supplier/csv',
    responseType: 'blob',
    timeout: 0,
  }),

  POSTSupplier: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/supplier'}),
  GETSupplierById: (supplierId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/supplier/${supplierId}/details`,
  }),
  PUTSupplierById: (supplierId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/supplier/${supplierId}`,
  }),
  PATCHSupplierStatus: (supplierId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/supplier/${supplierId}/archive`,
  }),
  DELETESupplier: (supplierId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/supplier/${supplierId}`,
  }),

  POSTSupplierContact: (supplierId: number | string): AxiosRequestConfig => ({
    method: 'POST',
    url: `/admin/supplier/${supplierId}/contact`,
  }),
  PUTSupplierContactById: (supplierId: number | string, contactId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/supplier/${supplierId}/contact/${contactId}`,
  }),
  DELETESupplierContact: (supplierId: number | string, contactId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/supplier/${supplierId}/contact/${contactId}`,
  }),
  GETSupplierContact: (supplierId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/supplier/${supplierId}/contacts`,
  }),

  /* SUPPLIERS UNITS */
  GETSuppliersUnits: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/supplier/units'}),
  GETSuppliersUnitsCSV: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/supplier/units/csv',
    responseType: 'blob',
    timeout: 0,
  }),

  POSTSuppliersUnit: (supplierId: number | string): AxiosRequestConfig => ({
    method: 'POST',
    url: `/admin/supplier-unit/${supplierId}/unit`,
  }),
  GETSupplierUnitById: (supplierUnitId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/supplier-unit/${supplierUnitId}/details`,
  }),
  PUTSupplierUnitById: (suppliersUnitId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/supplier-unit/${suppliersUnitId}`,
  }),
  PATCHSuppliersUnitStatus: (suppliersUnitId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/supplier-unit/${suppliersUnitId}/archive`,
  }),
  DELETESuppliersUnit: (suppliersUnitId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/supplier-unit/${suppliersUnitId}`,
  }),

  POSTSuppliersUnitContact: (suppliersUnitId: number | string): AxiosRequestConfig => ({
    method: 'POST',
    url: `/admin/supplier-unit/${suppliersUnitId}/contact`,
  }),
  PUTSuppliersUnitContactById: (suppliersUnitId: number | string, contactId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/supplier-unit/${suppliersUnitId}/contact/${contactId}`,
  }),
  DELETESuppliersUnitContact: (suppliersUnitId: number | string, contactId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/supplier-unit/${suppliersUnitId}/contact/${contactId}`,
  }),

  /* SUPPLIERS SERVICES */
  GETSuppliersServices: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/supplier-unit-service'}),
  GETSuppliersServicesCSV: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/supplier-unit-service/csv',
    timeout: 0,
  }),
  POSTSuppliersService: (supplierUnitId: number | string): AxiosRequestConfig => ({
    method: 'POST',
    url: `/admin/supplier-unit-service/${supplierUnitId}/supplier-org-unit-service`,
  }),
  PATCHSupplierService: (serviceId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/supplier-unit-service/${serviceId}`,
  }),
  GETSupplierServiceById: (serviceId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/supplier-unit-service/${serviceId}`,
  }),

  GETClientDocuments: (clientId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `http://localhost:3001/client/${clientId}/documents`,
  }),

  /* EXECUTIONS */
  GETExecutions: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/executions'}),
  GETCountExecutions: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/executions/count'}),
  GETExecutionsCSV: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/executions/csv',
    responseType: 'blob',
    timeout: 0,
  }),

  POSTExecution: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/executions'}),
  POSTExecutionsBatch: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/executions/batch'}),
  DELETEExecution: (executionId: number): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/executions/${executionId}`,
  }),
  DELETEExecutions: (): AxiosRequestConfig => ({
    method: 'DELETE',
    url: '/admin/executions',
  }),
  GETExecutionById: (executionId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/executions/${executionId}`,
  }),
  PUTExecutionById: (executionId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/executions/${executionId}`,
  }),
  PATCHExecutionStatusById: (executionId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/executions/${executionId}/status`,
  }),
  PUTExecutionMultipleUpdate: (): AxiosRequestConfig => ({
    method: 'PUT',
    url: '/admin/executions/multiple-update',
  }),
  GETServiceKindsByUnit: (clientUnitId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/executions/${clientUnitId}/servicesKind`,
  }),
  GETServicesByKindAndUnit: (clientUnitId: number | string, serviceKindId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/executions/${clientUnitId}/${serviceKindId}/services`,
  }),
  GETAllServicesForClientOrganisationUnit: (clientUnitId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/executions/${clientUnitId}/services`,
  }),
  GETAvailableServicesByKindAndUnit: (clientUnitId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/executions/${clientUnitId}/available-services`,
  }),
  GETHaulingAndDestinationByService: (clientUnitId: number | string, serviceId: number | string) => ({
    method: 'GET',
    url: `/executions/${clientUnitId}/${serviceId}/hauling-destination`,
  }),
  POSTMailToExecution: (executionId: number | string): AxiosRequestConfig => ({
    method: 'POST',
    url: `/executions/${executionId}/reminder-mail`,
  }),

  PATCHExecutionDocuments: (executionId: number | string) => ({
    method: 'PATCH',
    url: `/executions/${executionId}/documents`,
  }),
  GETExecutionComments: (executionId: number | string) => ({
    method: 'GET',
    url: `/executions/${executionId}/comments`,
  }),
  POSTExecutionComment: (executionId: number | string) => ({
    method: 'POST',
    url: `/executions/${executionId}/comments`,
  }),
  GETExecutionIssues: (executionId: number | string) => ({
    method: 'GET',
    url: `/executions/${executionId}/issues`,
  }),
  POSTExecutionIssue: (executionId: number | string): AxiosRequestConfig => ({
    method: 'POST',
    url: `/executions/${executionId}/issues`,
  }),
  GETExecutionHistory: (executionId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/executions/${executionId}/history`,
  }),

  GETLinkedExecutionById: (executionId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/executions/linked/${executionId}`,
  }),

  PATCHExecutionsGroupedAction: (): AxiosRequestConfig => ({
    method: 'PATCH',
    url: '/admin/executions/grouped-action',
  }),

  /* ISSUES */
  GETIssues: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/issues'}),
  GETIssuesCSV: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/issues/csv', timeout: 0,}),
  GETIssueById: (issueId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/issues/${issueId}`,
  }),
  PATCHIssueById: (issueId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/issues/${issueId}`,
  }),
  POSTIssue: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/issues'}),
  PATCHIssuesGroupedAction: (): AxiosRequestConfig => ({method: 'PATCH', url: '/admin/issues/grouped-action'}),
  GETIssueHistory: (issueId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/issues/${issueId}/history`,
  }),

  GETIssueKinds: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/issue-kinds/available',
  }),

  /* NOT FINISHED */
  PUTUpdatePurchaseStatus: (purchaseId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/purchases/${purchaseId}/status`,
  }),

  PUTUpdateBillTab: (billId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/purchases/${billId}/tab`,
  }),

  PUTUpdateApprovedExecution: (): AxiosRequestConfig => ({
    method: 'PUT',
    url: '/executions/update/approve',
  }),
  PUTUpdatTimedExecution: (): AxiosRequestConfig => ({
    method: 'PUT',
    url: '/executions/update/timed',
  }),
  /* -------- */

  /* WASTE FAMILIES */
  GETWasteFamilies: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/waste-families'}),
  POSTWasteFamily: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/waste-families'}),
  PUTWasteFamily: (wasteFamilyId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/waste-families/${wasteFamilyId}`,
  }),
  DELETEWasteFamily: (wasteFamilyId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/waste-families/${wasteFamilyId}`,
  }),

  /* USERS */
  GETUsers: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/user'}),
  GETUsersCSV: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/user/csv', responseType: 'blob', timeout: 0,}),
  GETUserById: (userId: number | string): AxiosRequestConfig => ({method: 'GET', url: `/admin/user/${userId}`}),
  PATCHUpdateUser: (userId: number | string): AxiosRequestConfig => ({method: 'PATCH', url: `/admin/user/${userId}`}),
  POSTUser: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/user'}),
  POSTCsvUsers: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/user/import-users'}),
  GETRefreshUsers: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/user/refresh-users'}),

  /* WASTE */
  GETAllWaste: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/waste'}),
  POSTWaste: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/waste'}),
  PUTWaste: (wasteId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/waste/${wasteId}`,
  }),
  DELETEWaste: (wasteId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/waste/${wasteId}`,
  }),

  /* CONTAINERS */
  GETContainers: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/container'}),
  POSTContainer: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/container'}),
  PUTContainer: (containerId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/container/${containerId}`,
  }),

  /* ACCOUNT */

  GETAccount: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/accounting-product-config'}),
  GETAccountById: (accountId): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/accounting-product-config/${accountId}`
  }),
  POSTAccount: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/accounting-product-config'}),
  PUTAccount: (accountId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/accounting-product-config/${accountId}`,
  }),
  DELETEAccount: (accountId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/accounting-product-config/${accountId}`,
  }),

  /* INDEXATION */
  GETIndexations: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/indexation'}),
  GETIndexation: (indexationId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/indexation/${indexationId}`
  }),
  PATCHIndexation: (indexationId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/indexation/${indexationId}`
  }),
  POSTIndexation: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/indexation'}),
  POSTIndexationRate: (indexationId: number | string): AxiosRequestConfig => ({
    method: 'POST',
    url: `/admin/indexation/${indexationId}/rate`
  }),

  /* INVOICING PERIOD */

  GETInvoicingPeriods: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/invoicing-period'}),
  POSTSetNextInvoicingPeriod: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/invoicing-period'}),
  GETInvoicingPeriodInvoicesCsv: (invoicingPeriodId: number): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/invoicing-period/${invoicingPeriodId}/csv`,
    responseType: 'blob',
    timeout: 0,
  }),
  GETInterfaceSupplierInvoicesSage: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/sage/manual-sage-purchases-transfer',
  }),
  GETInterfaceClientInvoicesSage: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/sage/manual-sage-invoices-transfer',
  }),
  GETInterfaceProvisionsSage: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/sage/manual-sage-provisions-transfer',
  }),

  /* SERVICE KINDS */
  GETServiceKinds: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/service-kind'}),
  POSTServiceKind: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/service-kind'}),
  PUTServiceKind: (serviceKindId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/service-kind/${serviceKindId}`,
  }),
  PATCHServiceKindPrices: (serviceKindId: number): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/service-kind/${serviceKindId}/prices`,
  }),

  /* ISSUE KINDS */
  GETAllIssueKinds: (): AxiosRequestConfig => ({method: 'GET', url: '/issue-kinds'}),
  POSTIssueKind: (): AxiosRequestConfig => ({method: 'POST', url: '/issue-kinds'}),
  DELETEIssueKind: (issueKindId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/issue-kinds/${issueKindId}`,
  }),
  PATCHIssueKind: (issueKindId: number): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/issue-kinds/${issueKindId}`,
  }),

  /* DOCUMENTS */
  GETDocuments: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/document',
  }),
  GETDocumentById: (documentId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/document/${documentId}`,
  }),
  POSTDocument: (): AxiosRequestConfig => ({
    method: 'POST',
    url: '/admin/document/upload',
    headers: {'Content-Type': 'multipart/form-data'},
  }),
  POSTClassicDocument: (): AxiosRequestConfig => ({
    method: 'POST',
    url: '/documents/upload',
    headers: {'Content-Type': 'multipart/form-data'},
  }),
  PUTDocument: (documentId: number | string): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/document/${documentId}`,
  }),
  DELETEDocument: (documentId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/document/${documentId}`,
  }),
  GETDocumentUrl: (documentId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/documents/${documentId}/stream`,
  }),

  /* PURCHASES */
  GETPurchases: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/purchase-statement',
  }),
  GETPurchasesCSV: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/purchase-statement/csv',
    responseType: 'blob',
    timeout: 0,
  }),
  GETPurchasesPiecesCSV: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/purchase-statement/pieces/csv',
    responseType: 'blob',
    timeout: 0,
  }),
  GETPurchaseDetails: (purchaseId: string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/purchase-statement/${purchaseId}/details`,
  }),
  DELETEPurchaseStatement: (purchaseId: string | number): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/purchase-statement/${purchaseId}`,
  }),
  POSTPurchaseStatement: (): AxiosRequestConfig => ({
    method: 'POST',
    url: '/admin/purchase-statement',
  }),
  DELETEPurchaseStatementInvoiceLines: (purchaseId: string | number): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/purchase-statement/${purchaseId}/invoice-lines`,
  }),
  PUTPurchaseStatementOrphanLine: (purchaseId: string | number): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/purchase-statement/${purchaseId}/invoice-lines`,
  }),
  POSTCancelPurchaseStatement: (purchaseId: string | number): AxiosRequestConfig => ({
    method: 'POST',
    url: `/admin/purchase-statement/${purchaseId}/cancel`,
  }),
  GETPrintPurchaseStatement: (purchaseId: string | number): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/purchase-statement/${purchaseId}/pdf`,
    responseType: 'blob'
  }),
  PATCHPurchaseStatement: (purchaseId: string | number): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/purchase-statement/${purchaseId}`,
  }),
  PATCHPurchaseStatementGroupedAction: (): AxiosRequestConfig => ({
    method: 'PATCH',
    url: '/admin/purchase-statement/grouped-action',
  }),
  PATCHPurchaseStatementResetAccountingTransferDate: (purchaseId: string | number): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/purchase-statement/${purchaseId}/reset-accounting-transfer-date`,
  }),

  /* BILLS */
  GETInvoices: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/invoices'}),
  GETInvoicesCSV: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/invoices/csv', timeout: 0,}),
  GETInvoicesPiecesCSV: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/invoices/pieces/csv'}),
  GETInvoiceDetails: (billId: string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/invoices/${billId}/details`,
  }),
  POSTInvoice: (): AxiosRequestConfig => ({
    method: 'POST',
    url: '/admin/invoices/generate',
  }),
  POSTInvoiceNestle: (): AxiosRequestConfig => ({
    method: 'POST',
    url: '/admin/invoices/generate/nestle',
  }),
  POSTInvoiceFromLines: (): AxiosRequestConfig => ({
    method: 'POST',
    url: '/admin/invoices/generate-from-lines',
  }),
  GETInvoicePdf: (billId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/invoices/${billId}/pdf`,
    responseType: 'blob',
  }),
  GETInvoiceMergePdf: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/invoices/pdf-merge',
    responseType: 'blob',
  }),
  GETInvoiceZipPdf: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/invoices/pdf-zip',
    responseType: 'blob',
  }),
  GETInvoiceInvoiceLinesCSV: (billId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/invoices/${billId}/invoice-lines/csv`,
    timeout: 0,
  }),
  DELETEInvoice: (billId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/invoices/${billId}`,
  }),
  DELETEInvoiceInvoiceLines: (billId: string | number): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/invoices/${billId}/invoice-lines`,
  }),
  PUTInvoiceOrphanLine: (billId: string | number): AxiosRequestConfig => ({
    method: 'PUT',
    url: `/admin/invoices/${billId}/invoice-lines`,
  }),
  PATCHInvoice: (billId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/invoices/${billId}`,
  }),
  PATCHInvoiceAddress: (billId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/invoices/${billId}/address`,
  }),
  PATCHInvoiceInvoiceLine: (invoiceId: number | string, invoiceLineId: number): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/invoices/${invoiceId}/invoice-lines/${invoiceLineId}`,
  }),
  GETInvoicesToSend: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/invoices/mailing-info'}),
  POSTSendInvoices: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/invoices/send-mail'}),
  POSTCancelInvoice: (invoiceId: number): AxiosRequestConfig => ({
    method: 'POST',
    url: `/admin/invoices/${invoiceId}/cancel`
  }),
  PATCHGroupedAction: (): AxiosRequestConfig => ({method: 'PATCH', url: '/admin/invoices/grouped-action'}),
  PATCHInvoiceResetAccountingTransferDate: (invoiceId: string | number): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/invoices/${invoiceId}/reset-accounting-transfer-date`
  }),

  /* EXECUTION LINES */
  GETExecutionLines: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/executions-line',
  }),
  GETExecutionLinesCSV: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/executions-line/csv',
    responseType: 'blob',
    timeout: 0,
  }),
  GETExecutionLinesReadyForInvoicing: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/executions-line/ready-for-invoicing',
  }),
  PATCHExecutionLineId: (executionLineId: number): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/executions-line/${executionLineId}`,
  }),
  GETExecutionLinesTotalAmount: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/executions-line/total-amount',
  }),
  PATCHExecutionLinesGroupedAction: (): AxiosRequestConfig => ({
    method: 'PATCH',
    url: '/admin/executions-line/grouped-action',
  }),
  PATCHExecutionLinesStatus: (): AxiosRequestConfig => ({
    method: 'PATCH',
    url: '/admin/executions-line/status/grouped-action',
  }),
  PATCHPurchaseStatementInvoiceLine: (
    purchaseStatementId: number | string,
    invoiceLineId: number
  ): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/purchase-statement/${purchaseStatementId}/invoice-lines/${invoiceLineId}`,
  }),
  GETExecutionLineForExecutionId: (executionId: number): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/executions-line/executions/${executionId}`,
  }),
  PATCHFixPrices: (
    serviceId: number | string,
  ): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/executions-line/fix-prices/${serviceId}`,
  }),

  /* JOBS INVOICE */
  GETJobsInvoice: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/job-invoice'}),
  GETJobInvoiceId: (jobInvoiceId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/job-invoice/${jobInvoiceId}`,
  }),
  DELETEJobInvoice: (jobInvoiceId: number): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/job-invoice/${jobInvoiceId}`,
  }),
  GETJobInvoiceZIP: (jobInvoiceId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/job-invoice/${jobInvoiceId}/zip`,
  }),
  GETJobInvoicePDF: (jobInvoiceId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/job-invoice/${jobInvoiceId}/pdf-merge`,
  }),
  GETJobInvoiceCSV: (jobInvoiceId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/invoices/${jobInvoiceId}/draft/csv`,
    timeout: 0,
  }),
  PATCHValidateJobInvoice: (jobInvoiceId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/job-invoice/${jobInvoiceId}/validate`,
  }),

  /* REPORTS */

  GETDownloadReport: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/reports/download',
    responseType: 'blob',
  }),

  GETReports: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/reports',
  }),

  GETReportById: (reportId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/reports/${reportId}`,
  }),

  PATCHReport: (reportId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/reports/${reportId}`,
  }),

  PATCHReportArchive: (reportId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/reports/${reportId}/archive`,
  }),

  CREATEReport: (): AxiosRequestConfig => ({
    method: 'POST',
    url: '/admin/reports',
  }),

  GETGenerateReport: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/reports/generate',
    responseType: 'blob',
  }),

  DELETEReport: (reportId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/reports/${reportId}`,
  }),

  /* JOBS */

  GETJobs: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/jobs',
  }),

  /* FILTERS */

  GETFilters: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/filters',
  }),

  /* UniversalHistories */
  GETHistories: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/universal-histories',
  }),

  GETHistory: (historyId: string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/universal-histories/${historyId}`,
  }),

  /* AUDITS */
  GETAudits: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/audit'}),
  POSTAudits: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/audit'}),
  PATCHAudits: (auditId: number | string): AxiosRequestConfig => ({method: 'PATCH', url: `/admin/audit/${auditId}`}),
  DELETEAudits: (auditId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/audit/${auditId}`,
  }),
  GETAuditsVersionUpdate: (auditId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/audit/${auditId}/save-version`,
  }),
  DELETESelectedAudits: (): AxiosRequestConfig => ({
    method: 'DELETE',
    url: '/admin/audit',
  }),
  GETAuditById: (auditId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/audit/${auditId}`,
  }),
  POSTMergeAudit: (): AxiosRequestConfig => ({
    method: 'POST',
    url: '/admin/audit/merge',
  }),

  /* AUDIT INFORMATION */
  GETAuditsInformation: (auditId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `admin/audit/information/${auditId}`
  }),
  POSTAuditsInformationContact: (): AxiosRequestConfig => ({
    method: 'POST',
    url: 'admin/audit/information/contact'
  }),
  PATCHAuditsInformationContact: (auditInformationId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `admin/audit/information/${auditInformationId}/contact`
  }),
  POSTAuditsInformationNeed: (): AxiosRequestConfig => ({
    method: 'POST',
    url: 'admin/audit/information/need'
  }),
  PATCHAuditsInformationNeed: (auditInformationId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `admin/audit/information/${auditInformationId}/need`
  }),
  POSTAuditsInformationData: (): AxiosRequestConfig => ({
    method: 'POST',
    url: 'admin/audit/information/data'
  }),
  PATCHAuditsInformationData: (auditInformationId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `admin/audit/information/${auditInformationId}/data`
  }),
  POSTAuditsInformationMap: (): AxiosRequestConfig => ({
    method: 'POST',
    url: 'admin/audit/information/site-map'
  }),
  PATCHAuditsInformationMap: (auditInformationId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `admin/audit/information/${auditInformationId}/site-map`
  }),
  POSTAuditsInformationObservation: (): AxiosRequestConfig => ({
    method: 'POST',
    url: 'admin/audit/information/observation'
  }),
  PATCHAuditsInformationObservation: (auditInformationId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `admin/audit/information/${auditInformationId}/observation`
  }),
  DELETEAuditsInformation: (auditInformationId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `admin/audit/information/${auditInformationId}`
  }),

  PATCHAuditsInformationPosition: (): AxiosRequestConfig => ({
    method: 'PATCH',
    url: 'admin/audit/information/reposition'
  }),
  GETPreAuditCSV: (auditId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `admin/audit/information/${auditId}/pre-audits/csv`,
    responseType: 'blob',
    timeout: 0,
  }),

  /* AUDIT TEMPLATES */

  GETAuditsTemplates: (): AxiosRequestConfig => ({method: 'GET', url: '/admin/audit/template'}),
  POSTAuditsTemplates: (): AxiosRequestConfig => ({method: 'POST', url: '/admin/audit/template'}),
  DELETEAuditsTemplates: (templateId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `/admin/audit/template/${templateId}`,
  }),
  DELETESelectedAuditsTemplates: (): AxiosRequestConfig => ({
    method: 'DELETE',
    url: '/admin/audit/templates',
  }),
  GETAuditTemplateById: (templateId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/audit/template/${templateId}`,
  }),

  /* AUDIT OPERATION */
  GetAuditsOperation: (auditId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `admin/audit/operation/${auditId}`
  }),

  GetAuditOperationDetails: (auditOperationId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `admin/audit/operation/${auditOperationId}/details`
  }),

  POSTAuditsOperation: (): AxiosRequestConfig => ({
    method: 'POST',
    url: 'admin/audit/operation'
  }),

  PATCHAuditsOperation: (auditOperationId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `admin/audit/operation/${auditOperationId}`,
  }),

  PATCHValidateAuditsOperation: (auditOperationId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `admin/audit/operation/${auditOperationId}/validate`,
  }),

  PATCHOptimizeAuditsOperation: (auditOperationId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `admin/audit/operation/${auditOperationId}/optimize`,
  }),

  DELETEAuditsOperation: (auditOperationId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `admin/audit/operation/${auditOperationId}`
  }),
  GETAuditCSV: (auditId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `admin/audit/operation/${auditId}/audits/csv`,
    responseType: 'blob',
    timeout: 0,
  }),
  GETPrecoAuditCSV: (auditId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `admin/audit/operation/${auditId}/preco/csv`,
    responseType: 'blob',
    timeout: 0,
  }),

  /* AUDIT OFFER */
  GETAuditsOffer: (auditId: number | string, version: number): AxiosRequestConfig => ({
    method: 'GET',
    url: `admin/audit/offer/${auditId}/${version}/getByAuditAndVersion`
  }),

  PATCHAuditsOffer: (): AxiosRequestConfig => ({
    method: 'PATCH',
    url: 'admin/audit/offer/createOrUpdate'
  }),

  DELETEAuditsOffer: (auditOfferId: number | string): AxiosRequestConfig => ({
    method: 'DELETE',
    url: `admin/audit/offer/${auditOfferId}`
  }),
  GETAuditsOfferCSV: (auditId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `admin/audit/offer/${auditId}/offer/csv`,
    responseType: 'blob',
    timeout: 0,
  }),

  GETAuditsOfferByAuditCSV: (auditId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `admin/audit/offer/${auditId}/getAuditOffersByAudit/csv`,
    responseType: 'blob',
    timeout: 0,
  }),

  GETHistoryByResourceId: (historyId: string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/universal-histories/resource/${historyId}`,
  }),

  PATCHPriceFilterActions: (auditId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/audit/offer/${auditId}/price-filter-actions`,
  }),

  GETAuditOffersByAudit: (auditId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/audit/offer/${auditId}/getAuditOffersByAudit`
  }),

  GETAuditsOffersSuppliersByAuditOfferId: (auditOfferId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/audit/offer/${auditOfferId}/audit-offers-suppliers`
  }),

  GETGroupedAuditsOffersSuppliers: (): AxiosRequestConfig => ({
    method: 'POST',
    url: '/admin/audit/offer/grouped-audit-offers-suppliers'
  }),

  POSTActivateOffersByAuditId: (auditId: number | string): AxiosRequestConfig => ({
    method: 'POST',
    url: `/admin/audit/${auditId}/activate`
  }),

  PatchGroupedAuditsOffersSuppliers: (): AxiosRequestConfig => ({
    method: 'PATCH',
    url: '/admin/audit/offer/grouped-supplier-offer'
  }),

  PATCHAuditOffer: (auditOfferId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/audit/offer/${auditOfferId}`
  }),

  GETSelectedSuppliers: (auditId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/audit/offer/${auditId}/selected-suppliers`
  }),

  PATCHServiceGroupedActions: (): AxiosRequestConfig => ({
    method: 'PATCH',
    url: '/admin/audit/offer/service-grouped-actions',
  }),

  PATCHPriceGroupedActions: (): AxiosRequestConfig => ({
    method: 'PATCH',
    url: '/admin/audit/offer/price-grouped-actions',
  }),


  PATCHUpdateValidate: (auditOperationId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/audit/operation/${auditOperationId}/updateValidDate`,
  }),

  PATCHServiceFilterActions: (auditId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/audit/offer/${auditId}/service-filter-actions`,
  }),

  /* Change Rate */
  GETChangeRatePeriods: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/change-rate/periods',
  }),
  GETChangeRate: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/change-rate',
  }),

  /* AuditOfferFollowUp */
  POSTAuditOfferFollowUp: (auditId: number | string): AxiosRequestConfig => ({
    method: 'POST',
    url: `/admin/audit/proposal/${auditId}`
  }),

  GETAuditsProposal: (auditId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/audit/proposal/${auditId}`
  }),

  GETAuditsProposalSuppliers: (auditId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/audit/proposal-supplier/${auditId}`
  }),

  GETAuditsOfferActivities: (auditId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/audit/proposal-supplier/${auditId}/activities`
  }),

  GETAuditsProposalDetails: (auditProposalId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/audit/proposal/${auditProposalId}/details`
  }),

  GETAuditsProposalSupplierDetails: (auditProposalSupplierId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/audit/proposal-supplier/${auditProposalSupplierId}/details`
  }),

  GETAuditsOfferFromProposal: (auditProposalId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/audit/proposal/${auditProposalId}/audit-offer`
  }),

  PATCHAuditpatchAuditsSupEmail: (auditProposalSupplierId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/audit/proposal-supplier/${auditProposalSupplierId}/update`
  }),

  GETAuditsOfferSupFromProposalSup: (auditProposalSupplierId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/audit/proposal-supplier/${auditProposalSupplierId}/audit-offer-supplier`
  }),

  PATCHAuditsOfferSupFromProposalSup: (auditProposalSupplierId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/audit/proposal-supplier/${auditProposalSupplierId}`
  }),

  PATCHAuditsSupSentAT: (auditProposalSupplierId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/audit/proposal-supplier/${auditProposalSupplierId}/sent-at`
  }),

  GETAllAuditsSupProposal: (auditProposalId: number | string): AxiosRequestConfig => ({
    method: 'GET',
    url: `/admin/audit/proposal-supplier/${auditProposalId}/supplier-units`
  }),


  /* INTERFACE */
  GETInterfaceEntities: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/interface',
  }),
  POSTInterfaceEntities: (): AxiosRequestConfig => ({
    method: 'POST',
    url: '/admin/interface',
  }),

  /* ORGANISATION CONFIG */
  GETAllOrganisationsConfig: (): AxiosRequestConfig => ({
    method: 'GET',
    url: '/admin/organisation-config',
  }),
  PATCHOrganisationConfig: (organisationConfigId: number | string): AxiosRequestConfig => ({
    method: 'PATCH',
    url: `/admin/organisation-config/${organisationConfigId}`,
  }),
};

export default APIAxios;
